import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useAuth from "./useAuth";
import useRefreshToken from "./useRefreshToken";
import { getAllowedTenantIdsFromToken } from "../utils/jwtPayload";
import {
  resolveEffectiveTenantId,
  storageKeyForSelectedTenant,
} from "../utils/resibotTenant";

const RESIBOT_API_URL =
  process.env.REACT_APP_RESIBOT_API_URL || "http://localhost:8000";

/**
 * ResiBot auth contract (see ResiBot api/services/auth.py):
 * - Authorization: Bearer <JWT> must be signed with the SAME secret as ResiBot JWT_SECRET.
 *   If ResiRent tokens come from Heroku (resirent-api), copy that server's JWT signing secret
 *   into ResiBot/.env — a placeholder local secret causes signature mismatch → HTTP 401.
 * - X-Tenant-ID must appear in JWT claims tenant_ids / tenant_id OR user must be admin.
 * - Claim resiBot: true (or resibot) is required unless role is admin (403 otherwise).
 *
 * X-Tenant-ID: JWT tenant_id / tenant_ids when present, else REACT_APP_RESIBOT_TENANT_ID, else
 * REACT_APP_RESIBOT_DEFAULT_TENANT (no hardcoded fallback). Picker choice is per-user in sessionStorage.
 */
async function parseErrorBody(res) {
  try {
    const data = await res.json();
    if (data?.detail !== undefined) {
      if (Array.isArray(data.detail)) {
        return data.detail.map((d) => d.msg || d.message || JSON.stringify(d)).join("; ");
      }
      return String(data.detail);
    }
    if (data?.error !== undefined) return String(data.error);
    if (data?.message) return String(data.message);
  } catch {
    /* ignore */
  }
  return res.statusText || "Request failed";
}

export function useResibotApi() {
  const { auth } = useAuth();
  const refresh = useRefreshToken();

  /** Keep latest bearer token without putting it in `request`'s useCallback deps — avoids
   *  identity churn after refresh() → useEffects that depend on `request` re-firing in a loop. */
  const accessTokenRef = useRef(auth?.accessToken);
  useEffect(() => {
    accessTokenRef.current = auth?.accessToken;
  }, [auth?.accessToken]);

  /** Single in-flight refresh when many parallel `request()` calls all get HTTP 401. */
  const refreshPromiseRef = useRef(null);

  const allowedTenantIds = useMemo(
    () => getAllowedTenantIdsFromToken(auth?.accessToken),
    [auth?.accessToken]
  );

  const envTenant = useMemo(() => {
    const v = process.env.REACT_APP_RESIBOT_TENANT_ID;
    return v && String(v).trim() ? String(v).trim().toLowerCase() : null;
  }, []);

  const [tenantId, setTenantIdState] = useState(() => {
    if (envTenant) return envTenant;
    if (auth?.accessToken) return resolveEffectiveTenantId(auth, auth.accessToken);
    return null;
  });

  useEffect(() => {
    if (envTenant) {
      setTenantIdState(envTenant);
      return;
    }
    if (!auth?.accessToken) return;
    setTenantIdState(resolveEffectiveTenantId(auth, auth.accessToken));
  }, [envTenant, auth?.accessToken, auth?.user, auth?.username]);

  useEffect(() => {
    if (envTenant || allowedTenantIds.length < 2) return;
    setTenantIdState((current) =>
      allowedTenantIds.includes(current) ? current : allowedTenantIds[0]
    );
  }, [allowedTenantIds, envTenant]);

  const setTenantId = useCallback(
    (id) => {
      const next = String(id || "").trim().toLowerCase();
      if (!next) return;
      setTenantIdState(next);
      try {
        sessionStorage.setItem(storageKeyForSelectedTenant(auth), next);
      } catch {
        /* quota / private mode */
      }
    },
    [auth]
  );

  const request = useCallback(
    async (path, options = {}) => {
      const {
        method = "GET",
        body,
        skipAuth = false,
        headers: extraHeaders = {},
      } = options;

      const url = `${RESIBOT_API_URL}${path.startsWith("/") ? path : `/${path}`}`;
      const headers = {
        ...extraHeaders,
        "X-Tenant-ID": tenantId,
      };
      if (body !== undefined) {
        headers["Content-Type"] = "application/json";
      }
      const bearer = accessTokenRef.current;
      if (!skipAuth) {
        if (!bearer) {
          throw Object.assign(
            new Error("Not signed in — open ResiRent in a logged-in tab."),
            { status: 401 }
          );
        }
        headers.Authorization = `Bearer ${bearer}`;
      }

      const doFetch = (token) => {
        const h = { ...headers };
        if (!skipAuth && token) {
          h.Authorization = `Bearer ${token}`;
        }
        return fetch(url, {
          method,
          headers: h,
          body: body !== undefined ? JSON.stringify(body) : undefined,
        });
      };

      let res = await doFetch(bearer);

      if (res.status === 401 && !skipAuth) {
        try {
          let p = refreshPromiseRef.current;
          if (!p) {
            p = refresh()
              .then((t) => t)
              .finally(() => {
                refreshPromiseRef.current = null;
              });
            refreshPromiseRef.current = p;
          }
          const newToken = await p;
          if (!newToken) {
            throw Object.assign(new Error("Session expired — please sign in again."), {
              status: 401,
            });
          }
          accessTokenRef.current = newToken;
          res = await doFetch(newToken);
        } catch (e) {
          if (e?.status === 401) throw e;
          throw Object.assign(new Error("Session expired — please sign in again."), {
            status: 401,
          });
        }
      }

      if (!res.ok) {
        const message = await parseErrorBody(res);
        throw Object.assign(new Error(message), { status: res.status });
      }

      if (res.status === 204) return null;
      return res.json();
    },
    [refresh, tenantId]
  );

  return {
    request,
    tenantId,
    setTenantId,
    allowedTenantIds,
    authReady: envTenant ? true : Boolean(auth?.accessToken),
    baseUrl: RESIBOT_API_URL,
  };
}

export { RESIBOT_API_URL };
